// src/components/layout/SectionTabs.tsx
import React from "react";
import clsx from "clsx";
import { NavLink, useParams } from "react-router-dom";

import { A } from "../../styles/appleClasses";
import { companyRoute } from "../../utils/routeHelpers";

export type SectionTab = {
  label: string;
  to: string;
  end?: boolean;
  disabled?: boolean;
};

export type SectionTabsProps = {
  tabs: SectionTab[];
  ariaLabel: string;
  className?: string;
  tabClassName?: string;
  companyScoped?: boolean;
};

const tabsWrap =
  "inline-flex items-center rounded-xl border border-gray-200 bg-gray-50 p-1";

const tabBase =
  "px-3 py-1.5 rounded-xl border border-transparent " +
  `${A.body} font-medium transition whitespace-nowrap ` +
  "focus:outline-none focus:ring-2 focus:ring-appleblueD";

function tabCls(active: boolean, disabled?: boolean, className?: string) {
  return clsx(
    tabBase,
    active
      ? "bg-white shadow-sm border-gray-200 text-gray-900"
      : "text-gray-600 hover:text-gray-900 hover:bg-white/70",
    disabled && "pointer-events-none opacity-40",
    className,
  );
}

function resolveTabTo(
  to: string,
  companyId: string | undefined,
  companyScoped: boolean,
) {
  if (!companyScoped) return to;
  if (!to.startsWith("/")) return to;
  return companyId ? companyRoute(companyId, to) : to;
}

export default function SectionTabs({
  tabs,
  ariaLabel,
  className = "",
  tabClassName = "",
  companyScoped = true,
}: SectionTabsProps) {
  const { company_id } = useParams<{ company_id?: string }>();

  if (!tabs.length) return null;

  return (
    <nav
      className={clsx("overflow-x-auto pb-1", className)}
      aria-label={ariaLabel}
    >
      <div className={tabsWrap}>
        {tabs.map((tab) => {
          if (tab.disabled) {
            return (
              <span
                key={`${tab.label}-${tab.to}`}
                aria-disabled="true"
                className={tabCls(false, true, tabClassName)}
              >
                {tab.label}
              </span>
            );
          }

          return (
            <NavLink
              key={`${tab.label}-${tab.to}`}
              to={resolveTabTo(tab.to, company_id, companyScoped)}
              end={tab.end ?? false}
              className={({ isActive }) =>
                tabCls(isActive, false, tabClassName)
              }
            >
              {tab.label}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}